"use client"

import {
    getTypographyStyles,
    type TypographyKeys
} from "@/lib/utils/typography/getTypographyStyles"
import { clsx } from "@/src/utils/clsx"
import React from "react"

interface RadioProps {
    name: string
    label: string | React.ReactNode
    value: string
    checked?: boolean
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabled?: boolean
    required?: boolean
    className?: string
    containerClassName?: string
    id?: string
    typography?: TypographyKeys
    suffix?: React.ReactNode
    inputClassName?: string
    inseta11y?: { before: string }
    hideBasketFunctionality?: boolean
}

export default function Radio({
    name,
    label,
    value,
    checked = false,
    onChange,
    disabled = false,
    required = false,
    className = "",
    containerClassName = "",
    id,
    typography = ".Trade_Gothic_Text_14_150_0_300",
    suffix,
    inputClassName = "",
    hideBasketFunctionality = false,
    inseta11y = {
        before: "" //"before:absolute before:inset-0 inset-0 before:-inset-x-12 before:-bottom-12 before:-top-16 before:content-['']" Helps with a11y button press areas
    },
    ...props
}: RadioProps) {
    const radioId = id || `${name}-${value}`
    return (
        <label
            id={`${radioId}-label`}
            htmlFor={radioId}
            className={clsx(
                "relative flex w-fit cursor-pointer select-none items-start gap-8 text-col-text-primary",
                inseta11y.before,
                disabled && "cursor-not-allowed opacity-50",
                containerClassName,
                getTypographyStyles(typography)
            )}
        >
            <div className="relative mt-2 flex items-center">
                {!hideBasketFunctionality && (
                    <input
                        type="radio"
                        id={radioId}
                        name={name}
                        value={value}
                        checked={checked}
                        onChange={onChange}
                        disabled={disabled}
                        required={required}
                        className={clsx(
                            "h-4 w-4 flex-shrink-0 cursor-pointer appearance-none rounded-full transition-colors duration-200",
                            checked
                                ? "bg-black"
                                : "border-[1px] border-col-border-neutral bg-col-bg-primary", // Unselected state: border with white background
                            disabled && "cursor-not-allowed opacity-50",
                            inputClassName
                        )}
                        style={{
                            width: "16px",
                            height: "16px",
                            aspectRatio: "1/1"
                        }}
                        aria-describedby={label ? `${radioId}-label` : undefined}
                        {...props}
                    />
                )}
                {/* Inner dot for selected state */}
                {checked && !hideBasketFunctionality && (
                    <div
                        className="absolute z-10 flex-shrink-0 bg-white"
                        style={{
                            width: "6px",
                            height: "6px",
                            aspectRatio: "1/1",
                            borderRadius: "1000px",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)"
                        }}
                    />
                )}
            </div>

            {label}
            {required && <span className="ml-1 text-col-text-alert">*</span>}

            {suffix && suffix}
        </label>
    )
}
